/**
 * useAnalyticsData - Custom hook for analytics data fetching
 * 
 * Wraps useAdminData for activity trends API with:
 * - Configurable caching (5-minute for activity chart, 1-hour for trends)
 * - Period parameter support (7d, 30d, 90d, custom)
 * - Data transformation for chart consumption
 * - Comparison metrics calculation
 * 
 * Requirements: 2.3, 2.6
 */

import { useMemo } from 'react';
import { useAdminData } from '../components/shared/useAdminData';
import { API_ENDPOINTS, buildApiUrl } from '../config/api-endpoints';
import AdminCache from '../components/shared/AdminCache';

/**
 * Hook for fetching activity trends data
 * 
 * @param {string} period - Time period: '7d', '30d', '90d', or 'custom'
 * @param {object} options - Configuration options
 * @param {string} options.startDate - Start date for custom period (ISO format)
 * @param {string} options.endDate - End date for custom period (ISO format)
 * @param {boolean} options.useShortCache - Use 5-minute cache (for activity chart) vs 1-hour (for trends)
 * @returns {object} - { data, loading, error, refresh, fromCache }
 */
export const useAnalyticsData = (period = '30d', options = {}) => {
  const {
    startDate = null,
    endDate = null,
    useShortCache = false
  } = options;

  // Build query parameters
  const params = useMemo(() => {
    const queryParams = { period };
    
    // Add custom date range if provided
    if (period === 'custom' && startDate && endDate) {
      queryParams.startDate = startDate;
      queryParams.endDate = endDate;
    }
    
    return queryParams;
  }, [period, startDate, endDate]);

  // Determine cache TTL based on use case
  // Activity chart (Dashboard): 5 minutes for near real-time feel
  // Trend chart (Analytics page): 1 hour since data is historical
  const cacheTTL = useShortCache 
    ? AdminCache.TTL.long        // 5 minutes
    : 60 * 60 * 1000;            // 1 hour

  // Transform function to format data for chart consumption
  const transform = useMemo(() => (responseData) => {
    if (!responseData || !responseData.success) {
      return null;
    }

    const { data, comparison } = responseData;

    // Format data for chart components
    return {
      // Time series data for line charts
      dates: data.dates || [],
      active: data.active || [],
      inactive: data.inactive || [],
      zombie: data.zombie || [],
      total: data.total || [],
      
      // Comparison metrics for display
      comparison: {
        activeChange: comparison?.activeChange || 0,
        inactiveChange: comparison?.inactiveChange || 0,
        totalChange: comparison?.totalChange || 0,
        period: comparison?.period || 'week'
      },
      
      // Metadata
      period,
      startDate: data.startDate || startDate,
      endDate: data.endDate || endDate,
      dataPoints: data.dates?.length || 0
    };
  }, [period, startDate, endDate]);

  // Fetch data using useAdminData
  const result = useAdminData(
    `analytics-trends-${period}`,
    buildApiUrl(API_ENDPOINTS.ADMIN.ANALYTICS_HANDLER, '/analytics/activity-trends'),
    {
      params,
      ttl: cacheTTL,
      transform,
      staleWhileRevalidate: true,
      fallbackData: {
        dates: [],
        active: [],
        inactive: [],
        zombie: [],
        total: [],
        comparison: {
          activeChange: 0,
          inactiveChange: 0,
          totalChange: 0,
          period: 'week'
        },
        period,
        dataPoints: 0
      }
    }
  );

  return result;
};

/**
 * Hook for fetching current activity snapshot (for activity chart on Dashboard)
 * Uses shorter cache TTL (5 minutes) for near real-time updates
 * 
 * @returns {object} - { data, loading, error, refresh, fromCache }
 */
export const useActivitySnapshot = () => {
  return useAnalyticsData('7d', { useShortCache: true });
};

/**
 * Hook for fetching activity trends (for trend chart on Analytics page)
 * Uses longer cache TTL (1 hour) since historical data changes slowly
 * 
 * @param {string} period - Time period: '7d', '30d', '90d', or 'custom'
 * @param {object} options - Configuration options
 * @returns {object} - { data, loading, error, refresh, fromCache }
 */
export const useActivityTrends = (period = '30d', options = {}) => {
  return useAnalyticsData(period, { ...options, useShortCache: false });
};

/**
 * Helper function to format comparison metrics for display
 * 
 * @param {object} comparison - Comparison data from API
 * @returns {object} - Formatted comparison metrics
 */
export const formatComparisonMetrics = (comparison) => {
  if (!comparison) {
    return {
      active: { value: 0, text: 'No change', color: 'neutral' },
      inactive: { value: 0, text: 'No change', color: 'neutral' },
      total: { value: 0, text: 'No change', color: 'neutral' }
    };
  }

  const formatMetric = (change, label) => {
    const value = Math.abs(change);
    const sign = change > 0 ? '+' : change < 0 ? '-' : '';
    const color = change > 0 ? 'success' : change < 0 ? 'error' : 'neutral';
    const text = `${sign}${value.toFixed(1)}% ${label} vs last ${comparison.period}`;
    
    return { value: change, text, color, sign };
  };

  return {
    active: formatMetric(comparison.activeChange, 'active users'),
    inactive: formatMetric(comparison.inactiveChange, 'inactive users'),
    total: formatMetric(comparison.totalChange, 'total users')
  };
};

/**
 * Helper function to calculate data point intervals for chart display
 * 
 * @param {string} period - Time period
 * @returns {string} - Interval description
 */
export const getDataPointInterval = (period) => {
  switch (period) {
    case '7d':
      return 'daily';
    case '30d':
      return 'weekly';
    case '90d':
      return 'weekly';
    case 'custom':
      return 'varies';
    default:
      return 'daily';
  }
};

/**
 * Helper function to validate custom date range
 * 
 * @param {string} startDate - Start date (ISO format)
 * @param {string} endDate - End date (ISO format)
 * @returns {object} - { valid: boolean, error: string|null }
 */
export const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return { valid: false, error: 'Both start and end dates are required' };
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { valid: false, error: 'Invalid date format' };
  }

  if (start > end) {
    return { valid: false, error: 'Start date must be before end date' };
  }

  const maxRange = 365 * 24 * 60 * 60 * 1000; // 1 year
  if (end - start > maxRange) {
    return { valid: false, error: 'Date range cannot exceed 1 year' };
  }

  return { valid: true, error: null };
};

export default useAnalyticsData;
